/**
 * SpotlightCampaigns — employer admin page for creating and managing
 * "Priority Roles" micro-site campaigns. Full CRUD in one page:
 *   · Left column: list of existing campaigns with published/draft chip
 *     and view counts
 *   · Right column: create / edit form for the selected campaign
 *
 * Public URL for each is /spotlight/<companySlug>/<campaignSlug>.
 * The page shows the shareable URL prominently with a copy button so
 * recruiters can share links inside minutes.
 */
import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import SectionsEditor from "@/components/SectionsEditor";
import CampaignAnalyticsPanel from "@/components/CampaignAnalyticsPanel";
import TierLock from "@/components/TierLock";
import { Link, useNavigate } from "react-router-dom";
import {
    Plus, Trash2, Copy, ExternalLink, Sparkles, Save, Eye, EyeOff,
    ArrowLeft, Image as ImageIcon, Palette, Users as UsersIcon,
    LayoutTemplate, FileText, Search, BarChart3, Send, Type as TypeIcon,
    Video as VideoIcon, Zap,
} from "lucide-react";
import { trackSpotlightEvent } from "@/lib/spotlightEvents";

const EMPTY = () => ({
    name: "",
    slug: "",
    headline: "",
    subheadline: "",
    hero_image_url: "",
    cta_label: "Apply now",
    cta_url: "",
    accent_color: "#002FA7",
    testimonials: [],
    job_filter: { highlighted_only: true, department: null, work_mode: null, urgency: null, job_ids: [] },
    is_published: false,
    hero_style: "image",
    hero_video_url: "",
    body_font: "",
    custom_css: "",
    og_image_url: "",
    og_title: "",
    og_description: "",
    remove_portalwiz_footer: false,
    custom_domain: "",
    sections: [],
    list_in_directory: false,
    directory_tags: [],
    is_featured: false,
});

const TABS = [
    { id: "content",   label: "Content",   icon: FileText },
    { id: "sections",  label: "Sections",  icon: LayoutTemplate },
    { id: "design",    label: "Design",    icon: Palette },
    { id: "seo",       label: "SEO",       icon: Search },
    { id: "analytics", label: "Analytics", icon: BarChart3 },
    { id: "publish",   label: "Publish",   icon: Send },
];

/**
 * BoostAction — inline "Upgrade this role to Featured" CTA rendered under
 * each Live + directory-listed + non-featured campaign in the list.
 *
 * · Free / no `featured_slot`: routes to /pricing/spotlight with attribution
 *   params (from=boost, campaign_id) so the funnel view can trace the
 *   conversion path.
 * · Starter+ / has `featured_slot`: single-click PATCH `is_featured=true`
 *   and the row optimistically updates with the FEATURED badge.
 */
function BoostAction({ campaign, entsHasFeaturedSlot, planKey, onBoosted }) {
    const nav = useNavigate();
    const [busy, setBusy] = useState(false);

    const handleClick = async (e) => {
        e.stopPropagation();
        trackSpotlightEvent("boost_button_click", {
            campaign_id: campaign.id,
            plan_key: planKey || null,
            source: "campaigns_list",
        });
        if (!entsHasFeaturedSlot) {
            // Free tier — route to pricing with attribution.
            nav(`/pricing/spotlight?from=boost&campaign_id=${encodeURIComponent(campaign.id)}`);
            return;
        }
        setBusy(true);
        try {
            const { data } = await api.patch(`/spotlight-campaigns/${campaign.id}`, { is_featured: true });
            onBoosted?.(data);
            toast.success("Boosted! Your campaign now has Featured placement.");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="px-3 py-1.5 border-t border-dashed border-amber-200 bg-gradient-to-r from-amber-50/70 to-transparent flex items-center justify-between gap-2">
            <div className="text-[10px] font-mono uppercase tracking-widest text-amber-900 inline-flex items-center gap-1">
                <Zap className="w-3 h-3" />
                {entsHasFeaturedSlot ? "Boost to Featured" : "Upgrade to Boost"}
            </div>
            <button
                type="button"
                onClick={handleClick}
                disabled={busy}
                className="h-6 px-2 border border-amber-500 bg-white hover:bg-amber-100 text-[10px] font-mono uppercase tracking-widest text-amber-800 inline-flex items-center gap-1 disabled:opacity-50"
                data-testid={`campaign-boost-btn-${campaign.id}`}
            >
                {busy ? "…" : (entsHasFeaturedSlot ? "Boost" : "Upgrade")} <Sparkles className="w-2.5 h-2.5" />
            </button>
        </div>
    );
}

export default function SpotlightCampaigns() {
    const { user } = useAuth();
    const [items, setItems] = useState([]);
    const [companySlug, setCompanySlug] = useState("");
    const [activeId, setActiveId] = useState(null);
    const [form, setForm] = useState(EMPTY());
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState("content");
    const [ents, setEnts] = useState(null);

    const load = async () => {
        try {
            const [{ data: list }, { data: co }, entsRes] = await Promise.all([
                api.get("/spotlight-campaigns"),
                api.get("/company").catch(() => ({ data: {} })),
                api.get("/spotlight-campaigns-entitlements").catch(() => ({ data: null })),
            ]);
            setItems(list?.items || []);
            setCompanySlug(co?.company_slug || co?.slug || user?.company_slug || "");
            if (entsRes.data) setEnts(entsRes.data);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const active = useMemo(() => items.find((i) => i.id === activeId), [items, activeId]);
    useEffect(() => {
        if (active) setForm({ ...EMPTY(), ...active, job_filter: { ...EMPTY().job_filter, ...(active.job_filter || {}) } });
        else setForm(EMPTY());
    }, [active]);

    const publicUrl = (slug) => `${window.location.origin}/spotlight/${companySlug || "your-company"}/${slug}`;
    const isRealSlug = Boolean(companySlug);

    const startNew = () => { setActiveId(null); setForm(EMPTY()); };

    const save = async () => {
        if (!form.name?.trim() || !form.headline?.trim()) {
            toast.error("Name and headline are required");
            return;
        }
        setSaving(true);
        try {
            const payload = { ...form };
            // Clean up empty strings that Pydantic would reject on optional URL fields.
            for (const k of ["subheadline", "hero_image_url", "cta_label", "cta_url", "accent_color",
                              "hero_video_url", "body_font", "custom_css",
                              "og_image_url", "og_title", "og_description", "custom_domain"]) {
                if (payload[k] === "") payload[k] = null;
            }
            if (activeId) {
                const { data } = await api.patch(`/spotlight-campaigns/${activeId}`, payload);
                setItems((cur) => cur.map((i) => (i.id === activeId ? data : i)));
                toast.success("Saved");
            } else {
                const { data } = await api.post("/spotlight-campaigns", payload);
                setItems((cur) => [data, ...cur]);
                setActiveId(data.id);
                if (isRealSlug) {
                    toast.success(`Created — public URL: /spotlight/${companySlug}/${data.slug}`);
                } else {
                    toast.success("Created — set a company slug to enable sharing");
                }
            }
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSaving(false); }
    };

    const remove = async () => {
        if (!activeId) return;
        if (!window.confirm("Delete this campaign? The public URL will 404 immediately.")) return;
        try {
            await api.delete(`/spotlight-campaigns/${activeId}`);
            setItems((cur) => cur.filter((i) => i.id !== activeId));
            setActiveId(null);
            setForm(EMPTY());
            toast.success("Deleted");
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const togglePublish = () => setForm((f) => ({ ...f, is_published: !f.is_published }));

    const addTestimonial = () => setForm((f) => ({
        ...f, testimonials: [...(f.testimonials || []), { name: "", role: "", quote: "", avatar_url: "" }],
    }));
    const updTestimonial = (i, patch) => setForm((f) => ({
        ...f, testimonials: f.testimonials.map((t, idx) => idx === i ? { ...t, ...patch } : t),
    }));
    const delTestimonial = (i) => setForm((f) => ({
        ...f, testimonials: f.testimonials.filter((_, idx) => idx !== i),
    }));

    const copyUrl = () => {
        if (!active?.slug) return;
        navigator.clipboard.writeText(publicUrl(active.slug));
        toast.success("Public URL copied");
    };

    if (loading) return <div className="max-w-6xl mx-auto p-6 text-xs font-mono uppercase tracking-widest text-muted-foreground">Loading…</div>;

    return (
        <div className="max-w-[1400px] mx-auto px-3 sm:px-4 py-3 sm:py-5 space-y-4" data-testid="spotlight-campaigns-page">
            <div>
                <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-mono text-ikb">
                    <Sparkles className="w-3.5 h-3.5" /> Priority Roles
                </div>
                <h1 className="font-display font-black text-2xl sm:text-4xl mt-2 leading-tight">Spotlight campaigns</h1>
                <p className="text-xs sm:text-sm text-muted-foreground mt-1 max-w-2xl">
                    Branded micro-sites showcasing your urgent hires. Share the link on LinkedIn, in email, or on your careers homepage. Each campaign pulls from your highlighted jobs — set a spotlight-until date on the job to auto-expire.
                </p>
                {ents && (
                    <div className="mt-3 inline-flex items-center gap-3 border border-border bg-white px-3 py-2 flex-wrap" data-testid="tier-banner">
                        <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Your tier</span>
                        <span className="text-[11px] font-mono uppercase tracking-widest font-bold text-ikb">{ents.plan_key}</span>
                        <span className="text-[10px] font-mono text-muted-foreground">·</span>
                        <span className="text-[11px] font-mono">
                            {ents.published_count} / {ents.campaigns_limit == null ? "∞" : ents.campaigns_limit} published
                        </span>
                        {ents.plan_key !== "enterprise" && (
                            <Link to="/pricing/spotlight" className="text-[11px] font-mono uppercase tracking-widest text-amber-800 hover:underline inline-flex items-center gap-1" data-testid="tier-upgrade-link">
                                Upgrade →
                            </Link>
                        )}
                    </div>
                )}
            </div>

            <div className="grid lg:grid-cols-[320px_1fr] gap-4">
                {/* ── List ──────────────────────────────────────────── */}
                <aside className="border border-border bg-white">
                    <header className="flex items-center gap-2 px-3 py-2 border-b border-border">
                        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground flex-1">Campaigns</div>
                        <Button size="sm" variant="outline" onClick={startNew} data-testid="campaign-new-btn" className="rounded-none h-7">
                            <Plus className="w-3 h-3 mr-1" /> New
                        </Button>
                    </header>
                    {items.length === 0 ? (
                        <div className="p-6 text-center text-xs text-muted-foreground">
                            No campaigns yet. Click <b>+ New</b> to start your first spotlight page.
                        </div>
                    ) : (
                        <ul className="divide-y divide-border max-h-[520px] overflow-y-auto pw-thin-scroll">
                            {items.map((it) => {
                                const canBoost = it.is_published && it.list_in_directory && !it.is_featured;
                                return (
                                <li key={it.id} data-testid={`campaign-row-${it.id}`} className="flex items-stretch">
                                    <button
                                        type="button"
                                        onClick={() => setActiveId(it.id)}
                                        className={`flex-1 text-left px-3 py-2 hover:bg-secondary/40 ${activeId === it.id ? "bg-ikb/5" : ""}`}
                                        data-testid={`campaign-item-${it.id}`}
                                    >
                                        <div className="flex items-center gap-2">
                                            <div className="text-sm font-medium truncate flex-1">{it.name}</div>
                                            {it.is_featured && (
                                                <span className="text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 border border-amber-500 text-amber-900 bg-gradient-to-r from-amber-100 to-amber-50 inline-flex items-center gap-1" data-testid={`campaign-featured-badge-${it.id}`}>
                                                    <Sparkles className="w-2.5 h-2.5" /> Featured
                                                </span>
                                            )}
                                            <span className={`text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 border ${
                                                it.is_published
                                                    ? "border-emerald-500 text-emerald-800 bg-emerald-50"
                                                    : "border-border text-muted-foreground"
                                            }`}>
                                                {it.is_published ? "Live" : "Draft"}
                                            </span>
                                        </div>
                                        <div className="text-[10px] font-mono text-muted-foreground truncate">
                                            /{it.slug} · {it.view_count || 0} views
                                        </div>
                                    </button>
                                    {it.is_published && companySlug && (
                                        <a
                                            href={publicUrl(it.slug)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={(e) => e.stopPropagation()}
                                            className="shrink-0 inline-flex items-center justify-center px-2 border-l border-border hover:bg-ikb/10 hover:text-ikb"
                                            title="Open public campaign page in a new tab"
                                            data-testid={`campaign-row-open-${it.id}`}
                                        >
                                            <ExternalLink className="w-3.5 h-3.5" />
                                        </a>
                                    )}
                                    {canBoost && (
                                        <BoostAction
                                            campaign={it}
                                            entsHasFeaturedSlot={ents?.featured_slot === true}
                                            planKey={ents?.plan_key}
                                            onBoosted={(updated) => setItems((cur) => cur.map((c) => (c.id === it.id ? { ...c, ...updated } : c)))}
                                        />
                                    )}
                                </li>
                                );
                            })}
                        </ul>
                    )}
                </aside>

                {/* ── Editor ────────────────────────────────────────── */}
                <section className="border border-border bg-white p-4 sm:p-6 space-y-5">
                    {active && (
                        <div className={`border border-dashed p-3 flex items-center gap-3 flex-wrap ${isRealSlug ? "border-border bg-secondary/30" : "border-amber-500 bg-amber-50"}`} data-testid="campaign-public-url-bar">
                            <div className={`text-[10px] font-mono uppercase tracking-widest shrink-0 ${isRealSlug ? "text-muted-foreground" : "text-amber-800"}`}>
                                {isRealSlug ? "Public URL" : "⚠ Set a company slug first"}
                            </div>
                            <div className="flex-1 min-w-0 text-xs font-mono truncate" title={publicUrl(active.slug)}>{publicUrl(active.slug)}</div>
                            <button
                                type="button"
                                onClick={copyUrl}
                                disabled={!isRealSlug}
                                className="h-7 px-2 border border-border hover:border-ikb text-[10px] font-mono uppercase tracking-widest inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
                                data-testid="campaign-copy-url"
                            >
                                <Copy className="w-3 h-3" /> Copy
                            </button>
                            {isRealSlug ? (
                                <a
                                    href={publicUrl(active.slug)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="h-7 px-2 border border-border hover:border-ikb text-[10px] font-mono uppercase tracking-widest inline-flex items-center gap-1"
                                    data-testid="campaign-open-url"
                                >
                                    <ExternalLink className="w-3 h-3" /> Open
                                </a>
                            ) : (
                                <span
                                    className="h-7 px-2 border border-border text-[10px] font-mono uppercase tracking-widest inline-flex items-center gap-1 opacity-40 cursor-not-allowed"
                                    data-testid="campaign-open-url"
                                    aria-disabled="true"
                                >
                                    <ExternalLink className="w-3 h-3" /> Open
                                </span>
                            )}
                        </div>
                    )}

                    {/* Tab bar */}
                    <div className="flex flex-wrap gap-0.5 border-b border-border -mx-4 sm:-mx-6 px-4 sm:px-6 pb-0" data-testid="campaign-tabs">
                        {TABS.map((t) => {
                            const Icon = t.icon;
                            const disabled = !activeId && (t.id === "analytics" || t.id === "sections");
                            return (
                                <button
                                    key={t.id}
                                    type="button"
                                    onClick={() => !disabled && setTab(t.id)}
                                    disabled={disabled}
                                    className={`h-9 px-3 border-b-2 -mb-px text-[11px] font-mono uppercase tracking-widest inline-flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed ${
                                        tab === t.id ? "border-ikb text-ikb" : "border-transparent hover:text-foreground text-muted-foreground"
                                    }`}
                                    data-testid={`campaign-tab-${t.id}`}
                                    title={disabled ? "Save this campaign first to unlock" : undefined}
                                >
                                    <Icon className="w-3.5 h-3.5" /> {t.label}
                                </button>
                            );
                        })}
                    </div>

                    {tab === "content" && (
                    <div className="space-y-5">
                    <div className="grid sm:grid-cols-2 gap-3">
                        <FieldBox label="Campaign name *">
                            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                                placeholder="Winter Hiring 2026" data-testid="campaign-name" className="rounded-none" />
                        </FieldBox>
                        <FieldBox label="Slug (auto if blank)">
                            <Input value={form.slug || ""} onChange={(e) => setForm({ ...form, slug: e.target.value })}
                                placeholder="winter-hiring-2026" data-testid="campaign-slug" className="rounded-none font-mono text-xs" />
                        </FieldBox>
                    </div>

                    <FieldBox label="Headline *">
                        <Input value={form.headline} onChange={(e) => setForm({ ...form, headline: e.target.value })}
                            placeholder="We're hiring 12 senior engineers this quarter"
                            data-testid="campaign-headline" className="rounded-none" />
                    </FieldBox>

                    <FieldBox label="Subheadline">
                        <Textarea value={form.subheadline || ""} onChange={(e) => setForm({ ...form, subheadline: e.target.value })}
                            placeholder="Join a team shipping AI-native tools recruiters actually love."
                            rows={2} data-testid="campaign-subheadline" className="rounded-none" />
                    </FieldBox>

                    <div className="grid sm:grid-cols-2 gap-3">
                        <FieldBox label="Hero image URL" icon={ImageIcon}>
                            <Input value={form.hero_image_url || ""} onChange={(e) => setForm({ ...form, hero_image_url: e.target.value })}
                                placeholder="https://…/hero.jpg" data-testid="campaign-hero-url" className="rounded-none text-xs" />
                        </FieldBox>
                        <FieldBox label="Accent color" icon={Palette}>
                            <div className="flex items-center gap-2">
                                <input type="color" value={form.accent_color || "#002FA7"}
                                    onChange={(e) => setForm({ ...form, accent_color: e.target.value })}
                                    className="w-10 h-10 border border-border p-0 cursor-pointer bg-white"
                                    data-testid="campaign-accent" />
                                <Input value={form.accent_color || ""} onChange={(e) => setForm({ ...form, accent_color: e.target.value })}
                                    className="rounded-none font-mono text-xs h-10 flex-1" />
                            </div>
                        </FieldBox>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-3">
                        <FieldBox label="CTA button label">
                            <Input value={form.cta_label || ""} onChange={(e) => setForm({ ...form, cta_label: e.target.value })}
                                placeholder="Apply now" data-testid="campaign-cta-label" className="rounded-none" />
                        </FieldBox>
                        <FieldBox label="CTA URL">
                            <Input value={form.cta_url || ""} onChange={(e) => setForm({ ...form, cta_url: e.target.value })}
                                placeholder="mailto:careers@…"
                                data-testid="campaign-cta-url" className="rounded-none text-xs" />
                        </FieldBox>
                    </div>

                    {/* Testimonials */}
                    <div className="pt-2 border-t border-border">
                        <div className="flex items-baseline justify-between mb-2">
                            <div className="inline-flex items-center gap-2 text-xs">
                                <UsersIcon className="w-3.5 h-3.5 text-ikb" />
                                <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Testimonials</span>
                                <span className="text-[10px] font-mono">{(form.testimonials || []).length}</span>
                            </div>
                            <button
                                type="button" onClick={addTestimonial}
                                className="h-7 px-2 border border-border hover:border-ikb text-[10px] font-mono uppercase tracking-widest inline-flex items-center gap-1"
                                data-testid="campaign-testimonial-add">
                                <Plus className="w-3 h-3" /> Add
                            </button>
                        </div>
                        {(form.testimonials || []).length === 0 ? (
                            <div className="text-[11px] text-muted-foreground border-l-2 border-border pl-2">No testimonials yet. Add 1-3 team quotes to boost conversion.</div>
                        ) : (
                            <ul className="space-y-2" data-testid="campaign-testimonials-list">
                                {form.testimonials.map((t, i) => (
                                    <li key={i} className="border border-border p-2 space-y-1" data-testid={`campaign-testimonial-${i}`}>
                                        <div className="grid sm:grid-cols-2 gap-1">
                                            <Input value={t.name || ""} onChange={(e) => updTestimonial(i, { name: e.target.value })}
                                                placeholder="Name" className="rounded-none h-8 text-xs" />
                                            <Input value={t.role || ""} onChange={(e) => updTestimonial(i, { role: e.target.value })}
                                                placeholder="Role, Team" className="rounded-none h-8 text-xs" />
                                        </div>
                                        <Textarea value={t.quote || ""} onChange={(e) => updTestimonial(i, { quote: e.target.value })}
                                            placeholder="Quote" rows={2} className="rounded-none text-xs" />
                                        <div className="flex items-center gap-2">
                                            <Input value={t.avatar_url || ""} onChange={(e) => updTestimonial(i, { avatar_url: e.target.value })}
                                                placeholder="Avatar URL" className="rounded-none h-8 text-xs flex-1" />
                                            <button type="button" onClick={() => delTestimonial(i)}
                                                className="h-8 w-8 grid place-items-center border border-border hover:border-rose-500 hover:text-rose-700"
                                                data-testid={`campaign-testimonial-del-${i}`}
                                                aria-label="Remove testimonial">
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    {/* Job filter */}
                    <div className="pt-2 border-t border-border">
                        <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-2">Which jobs to show</div>
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={form.job_filter?.highlighted_only ?? true}
                                onChange={(e) => setForm({ ...form, job_filter: { ...form.job_filter, highlighted_only: e.target.checked } })}
                                className="accent-ikb"
                                data-testid="campaign-highlighted-only"
                            />
                            <span className="text-sm">Only include jobs marked <b>Highlight</b></span>
                        </label>
                        <p className="text-[11px] text-muted-foreground border-l-2 border-border pl-2 mt-1">
                            Set a job&apos;s <em>Highlight</em> + <em>Spotlight until</em> via the bulk-edit dialog on the Jobs page.
                            Expired spotlights are auto-filtered out of the campaign.
                        </p>
                    </div>
                    </div>
                    )}

                    {tab === "sections" && (
                        <div data-testid="campaign-tab-sections-body">
                            {activeId ? (
                                <SectionsEditor
                                    sections={form.sections || []}
                                    onChange={(next) => setForm({ ...form, sections: next })}
                                />
                            ) : (
                                <div className="border border-dashed border-border p-8 text-center text-xs text-muted-foreground">
                                    Save this campaign first to unlock the Sections editor.
                                </div>
                            )}
                        </div>
                    )}

                    {tab === "design" && (
                        <div className="space-y-5" data-testid="campaign-tab-design-body">
                            <FieldBox label="Hero style" icon={LayoutTemplate}>
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
                                    {[
                                        { id: "image", label: "Image" },
                                        { id: "video", label: "Video" },
                                        { id: "gradient", label: "Gradient" },
                                        { id: "split", label: "Split" },
                                    ].map((opt) => (
                                        <button
                                            key={opt.id}
                                            type="button"
                                            onClick={() => setForm({ ...form, hero_style: opt.id })}
                                            className={`h-9 px-3 border text-[11px] font-mono uppercase tracking-widest ${
                                                form.hero_style === opt.id ? "border-ikb bg-ikb/5 text-ikb" : "border-border hover:border-foreground"
                                            }`}
                                            data-testid={`campaign-hero-style-${opt.id}`}
                                        >{opt.label}</button>
                                    ))}
                                </div>
                            </FieldBox>
                            {form.hero_style === "video" && (
                                <TierLock enabled={ents?.video_hero !== false} required="Pro" testId="lock-video">
                                    <FieldBox label="Hero video URL (YouTube / Vimeo / MP4)" icon={VideoIcon}>
                                        <Input value={form.hero_video_url || ""} onChange={(e) => setForm({ ...form, hero_video_url: e.target.value })}
                                            placeholder="https://youtube.com/watch?v=…" data-testid="campaign-hero-video-url" className="rounded-none text-xs" />
                                    </FieldBox>
                                </TierLock>
                            )}
                            <FieldBox label="Body font family (Google Fonts)" icon={TypeIcon}>
                                <Input value={form.body_font || ""} onChange={(e) => setForm({ ...form, body_font: e.target.value })}
                                    placeholder="Inter, Poppins, Playfair Display…" data-testid="campaign-body-font" className="rounded-none text-xs" />
                            </FieldBox>
                            <TierLock enabled={ents?.custom_css === true} required="Enterprise" testId="lock-custom-css">
                                <FieldBox label="Custom CSS (Enterprise tier)">
                                    <Textarea value={form.custom_css || ""} onChange={(e) => setForm({ ...form, custom_css: e.target.value })}
                                        rows={5} className="rounded-none text-xs font-mono"
                                        placeholder=".pw-spotlight-hero { … }"
                                        data-testid="campaign-custom-css" />
                                </FieldBox>
                            </TierLock>
                            <div className="border-t border-border pt-3">
                                <TierLock enabled={ents?.remove_footer !== false} required="Pro" testId="lock-remove-footer">
                                    <label className="flex items-center gap-2 cursor-pointer" data-testid="campaign-remove-footer-label">
                                        <input
                                            type="checkbox"
                                            checked={!!form.remove_portalwiz_footer}
                                            onChange={(e) => setForm({ ...form, remove_portalwiz_footer: e.target.checked })}
                                            className="accent-ikb"
                                            data-testid="campaign-remove-footer"
                                        />
                                        <span className="text-sm">Remove &quot;Powered by Portalwiz&quot; footer <span className="text-[10px] font-mono text-muted-foreground ml-1">(Pro tier)</span></span>
                                    </label>
                                </TierLock>
                            </div>
                        </div>
                    )}

                    {tab === "seo" && (
                        <div className="space-y-4" data-testid="campaign-tab-seo-body">
                            <FieldBox label="Sponsored Spotlight Directory" icon={Sparkles}>
                                <div className="flex items-center gap-3">
                                    <Switch
                                        checked={!!form.list_in_directory}
                                        onCheckedChange={(v) => setForm({ ...form, list_in_directory: !!v })}
                                        data-testid="campaign-list-in-directory-toggle"
                                    />
                                    <div className="text-xs">
                                        <div className="font-medium">List in public Portalwiz directory</div>
                                        <div className="text-[11px] text-muted-foreground">
                                            When on, this campaign appears on <span className="font-mono">/spotlight</span> — ranked by view count for organic discovery. Only takes effect when the campaign is published.
                                        </div>
                                    </div>
                                </div>
                                <div className="mt-3">
                                    <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Directory tags (comma-separated, max 8)</Label>
                                    <Input
                                        value={(form.directory_tags || []).join(", ")}
                                        onChange={(e) => {
                                            const parts = e.target.value.split(",").map((s) => s.trim().toLowerCase()).filter(Boolean).slice(0, 8);
                                            setForm({ ...form, directory_tags: parts });
                                        }}
                                        placeholder="engineering, remote, urgent, ai"
                                        data-testid="campaign-directory-tags"
                                        className="rounded-none text-xs font-mono mt-1"
                                        disabled={!form.list_in_directory}
                                    />
                                    <p className="text-[10px] font-mono text-muted-foreground mt-1">
                                        Visitors can filter directory listings by these tags.
                                    </p>
                                </div>
                                <div className="mt-4 pt-3 border-t border-dashed border-border">
                                    <TierLock enabled={ents?.featured_slot === true} required="Starter" testId="lock-featured-slot" campaignId={active?.id}>
                                        <div className="flex items-center gap-3">
                                            <Switch
                                                checked={!!form.is_featured}
                                                onCheckedChange={(v) => setForm({ ...form, is_featured: !!v })}
                                                data-testid="campaign-featured-slot-toggle"
                                                disabled={!form.list_in_directory}
                                            />
                                            <div className="text-xs">
                                                <div className="font-medium inline-flex items-center gap-1">
                                                    <Sparkles className="w-3.5 h-3.5 text-amber-500" /> Featured placement <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">(Starter+)</span>
                                                </div>
                                                <div className="text-[11px] text-muted-foreground mt-0.5">
                                                    Guaranteed top-of-directory placement + prominent <span className="font-mono">FEATURED</span> badge on your card. Requires <span className="font-mono">List in directory</span> to be on.
                                                </div>
                                            </div>
                                        </div>
                                    </TierLock>
                                </div>
                            </FieldBox>

                            <TierLock enabled={ents?.custom_domain === true} required="Pro" testId="lock-custom-domain">
                                <FieldBox label="Custom domain (Pro) — e.g. careers.acme.com">
                                    <Input value={form.custom_domain || ""} onChange={(e) => setForm({ ...form, custom_domain: e.target.value.toLowerCase().trim() })}
                                        placeholder="careers.your-company.com" data-testid="campaign-custom-domain" className="rounded-none text-xs font-mono" />
                                    <p className="text-[10px] font-mono text-muted-foreground mt-1">
                                        Point a CNAME from your domain to <span className="font-bold">spotlight.portalwiz.com</span>. We&apos;ll match the incoming Host header to render this campaign.
                                    </p>
                                </FieldBox>
                            </TierLock>
                            <FieldBox label="SEO / share title" icon={Search}>
                                <Input value={form.og_title || ""} onChange={(e) => setForm({ ...form, og_title: e.target.value })}
                                    placeholder={form.headline || "Winter Hiring 2026 — Acme Careers"}
                                    data-testid="campaign-og-title" className="rounded-none" />
                            </FieldBox>
                            <FieldBox label="SEO / share description">
                                <Textarea value={form.og_description || ""} onChange={(e) => setForm({ ...form, og_description: e.target.value })}
                                    rows={2} placeholder="Brief 1-2 line summary shown on Google & social previews"
                                    data-testid="campaign-og-description" className="rounded-none text-xs" />
                            </FieldBox>
                            <FieldBox label="OG image (1200×630 recommended)" icon={ImageIcon}>
                                <Input value={form.og_image_url || ""} onChange={(e) => setForm({ ...form, og_image_url: e.target.value })}
                                    placeholder="https://…/og.jpg" data-testid="campaign-og-image" className="rounded-none text-xs" />
                                {form.og_image_url && (
                                    <img src={form.og_image_url} alt="OG preview" className="mt-2 border border-border max-w-xs object-cover"
                                        onError={(e) => { e.currentTarget.style.display = "none"; }} />
                                )}
                            </FieldBox>
                        </div>
                    )}

                    {tab === "analytics" && activeId && (
                        <div data-testid="campaign-tab-analytics-body">
                            <CampaignAnalyticsPanel campaignId={activeId} />
                        </div>
                    )}

                    {tab === "publish" && (
                        <div className="space-y-3" data-testid="campaign-tab-publish-body">
                            <div className={`border p-4 ${form.is_published ? "border-emerald-500 bg-emerald-50" : "border-border bg-white"}`}>
                                <div className="flex items-center gap-3">
                                    <Switch checked={!!form.is_published} onCheckedChange={togglePublish}
                                        data-testid="campaign-publish-toggle-tab" />
                                    <div>
                                        <div className="text-sm font-medium inline-flex items-center gap-1">
                                            {form.is_published
                                                ? <><Eye className="w-3.5 h-3.5 text-emerald-700" /> Published — live at the public URL</>
                                                : <><EyeOff className="w-3.5 h-3.5 text-muted-foreground" /> Draft — not publicly reachable</>}
                                        </div>
                                        <div className="text-[11px] text-muted-foreground mt-0.5">
                                            {form.is_published
                                                ? "Anyone with the URL below can view. Toggle off to unpublish."
                                                : "Publishing makes /spotlight/... URL reachable by anyone. You can unpublish anytime."}
                                        </div>
                                    </div>
                                </div>
                            </div>
                            {active && (
                                <div className="text-xs text-muted-foreground">Views so far: <b className="font-mono">{active.view_count || 0}</b></div>
                            )}
                        </div>
                    )}

                    {/* Footer / actions */}
                    <div className="pt-3 border-t border-border flex items-center gap-2 flex-wrap">
                        <label className="inline-flex items-center gap-2 cursor-pointer mr-auto">
                            <Switch
                                checked={!!form.is_published}
                                onCheckedChange={togglePublish}
                                data-testid="campaign-publish-toggle"
                            />
                            <span className="text-sm inline-flex items-center gap-1">
                                {form.is_published
                                    ? <><Eye className="w-3.5 h-3.5 text-emerald-700" /> Published</>
                                    : <><EyeOff className="w-3.5 h-3.5 text-muted-foreground" /> Draft</>
                                }
                            </span>
                        </label>
                        {activeId && (
                            <>
                                <Button variant="outline" onClick={remove} data-testid="campaign-delete-btn"
                                    className="rounded-none border-rose-500 text-rose-700 hover:bg-rose-50">
                                    <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
                                </Button>
                                <Button variant="outline" onClick={startNew} data-testid="campaign-back-btn" className="rounded-none">
                                    <ArrowLeft className="w-3.5 h-3.5 mr-1" /> New
                                </Button>
                            </>
                        )}
                        <Button onClick={save} disabled={saving} data-testid="campaign-save-btn" className="rounded-none">
                            <Save className="w-3.5 h-3.5 mr-1" /> {saving ? "Saving…" : (activeId ? "Save changes" : "Create campaign")}
                        </Button>
                    </div>
                </section>
            </div>
        </div>
    );
}

function FieldBox({ label, icon: Icon, children }) {
    return (
        <div>
            <Label className="text-[10px] font-mono uppercase tracking-widest text-foreground/70 mb-1 inline-flex items-center gap-1">
                {Icon && <Icon className="w-3 h-3" />}{label}
            </Label>
            {children}
        </div>
    );
}
